/*
  <skigrin-sticky-atc> — koopbalk die onderaan verschijnt zodra de echte koopknop
  uit beeld is. Alleen mobiel (de rest regelt de CSS).

  De knop in de balk klikt de echte koopknop van Dawn aan. Zo loopt alles via
  product-form.js: gekozen variant, foutmeldingen, cart drawer. Geen tweede
  koopflow die uit de pas kan lopen.
*/
class SkigrinStickyAtc extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-sticky-add]');
    this.priceTarget = this.querySelector('[data-sticky-price]');
    this.compareTarget = this.querySelector('[data-sticky-compare]');
    this.badgeTarget = this.querySelector('[data-sticky-badge]');
    this.imageTarget = this.querySelector('[data-sticky-image]');

    if (this.button) {
      this.button.addEventListener('click', () => this.forwardClick());
    }

    this.watchBuyButtons();
    this.watchVariant();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.unsubscribe) this.unsubscribe();
  }

  get realSubmit() {
    // Dawn wikkelt het koopformulier hier niet in een <product-form>-element,
    // gewoon een <form> met een class — direct op de submit-knop zoeken dus,
    // met de <product-form>-variant als terugval voor het geval dat ooit wijzigt.
    return (
      document.querySelector('product-form form[action*="/cart/add"] [type="submit"]') ||
      document.querySelector('form[action*="/cart/add"] [type="submit"]')
    );
  }

  forwardClick() {
    const submit = this.realSubmit;
    if (submit) {
      submit.click();
      return;
    }
    // Geen koopformulier gevonden (bv. product uitverkocht zonder formulier):
    // breng de bezoeker dan gewoon naar de koopsectie.
    const anchor = document.querySelector('.product__info-container');
    if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  watchBuyButtons() {
    const anchor = document.querySelector('product-form') || document.querySelector('.product-form');
    if (!anchor || !('IntersectionObserver' in window)) {
      this.toggle(true);
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => this.toggle(!entry.isIntersecting)),
      { rootMargin: '0px 0px -40px 0px' }
    );
    this.observer.observe(anchor);
  }

  toggle(visible) {
    this.classList.toggle('skigrin-sticky--visible', visible);
    this.setAttribute('aria-hidden', visible ? 'false' : 'true');
    // Een weggeschoven balk mag geen tabstop zijn.
    if (this.button) this.button.tabIndex = visible ? 0 : -1;
  }

  watchVariant() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const html = event?.data?.html;

      // Dawn levert bij een variantwissel de verse sectie-HTML mee. Daar halen we
      // de al correct opgemaakte prijs uit, dus hoeven we hier niets te rekenen.
      const source = html && (html.querySelector(`#price-${event.data.sectionId}`) || html.querySelector('.price'));
      if (source && this.priceTarget) {
        // Eerst het Skigrin-prijsblok, dan Dawn's eigen prijsblok als terugval.
        const regular =
          source.querySelector('[data-price-current]') ||
          source.querySelector('.price-item--sale, .price-item--regular');
        const compare =
          source.querySelector('[data-price-compare]') ||
          source.querySelector('s.price-item--regular, .price-item--regular.price__sale');

        if (regular) this.priceTarget.textContent = regular.textContent.trim();
        if (this.compareTarget) {
          const text = compare ? compare.textContent.trim() : '';
          this.compareTarget.textContent = text;
          this.compareTarget.hidden = text === '';
        }
      }

      // De rauwe variant-data (prijzen in centen, featured_image) komt los mee van
      // Dawn, dus daarvoor hoeven we niets uit de HTML te plukken. Onafhankelijk van
      // het HTML-gebaseerde prijsblok hierboven, zodat een gemiste match daar de
      // badge/foto-update niet blokkeert.
      const variant = event?.data?.variant;
      if (!variant) return;

      if (this.badgeTarget) {
        const compareCents = variant.compare_at_price;
        const priceCents = variant.price;
        const hasDiscount = compareCents > priceCents;
        this.badgeTarget.hidden = !hasDiscount;
        if (hasDiscount) {
          const percent = Math.floor(((compareCents - priceCents) * 100) / compareCents);
          this.badgeTarget.textContent = `-${percent}%`;
        }
      }

      if (this.imageTarget && variant.featured_image?.src) {
        // Shopify's rauwe variant-JSON levert de CDN-url zonder breedte; die plakken
        // we erachter, anders laadt de browser de volledige productfoto.
        const src = variant.featured_image.src;
        const sized = src + (src.includes('?') ? '&' : '?') + 'width=160';
        this.imageTarget.src = sized;
        this.imageTarget.srcset = '';
        this.imageTarget.alt = variant.featured_image.alt || variant.name || '';
      }
    });
  }
}

if (!customElements.get('skigrin-sticky-atc')) {
  customElements.define('skigrin-sticky-atc', SkigrinStickyAtc);
}
